import { useEffect, useState } from "react";
import { getUnreadCount } from "../api/notificationInbox";

// The unread count shown on the bell (ticket 2.8.1). One shared store, so the two bells (desktop and mobile header), the panel and
// the page always show the same number, and only one request runs at a time.
//   - It refreshes every 30 seconds while the tab is visible, when the tab gets focus, and whenever something in the inbox changes.
//   - Another open tab is told through a BroadcastChannel (or the storage event where that is missing), so the count follows
//     what the user does in the other tab straight away.
//   - A failed request keeps the last count and flags `failed`; it never throws into the top bar.

const POLL_MS = 30000;
const CHANNEL = "pipaan-inbox";
const STORAGE_KEY = "pipaan-inbox-changed";

let state = { count: null, failed: false };
const listeners = new Set();
let timer = null;
let channel = null;
let inFlight = false;

const emit = () => listeners.forEach((fn) => fn(state));
const set = (patch) => {
  state = { ...state, ...patch };
  emit();
};

const hasToken = () => !!(sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken"));

export async function refreshUnread() {
  if (inFlight || !hasToken()) return;
  inFlight = true;
  try {
    const result = await getUnreadCount();
    set({ count: Number(result.unread_count) || 0, failed: false });
  } catch (e) {
    set({ failed: true });
  } finally {
    inFlight = false;
  }
}

// Called after the user reads, un-reads or dismisses a notice: updates this tab at once and tells the other tabs.
export function inboxChanged(unreadCount) {
  if (typeof unreadCount === "number") set({ count: unreadCount, failed: false });
  try {
    if (channel) channel.postMessage({ type: "changed" });
    else localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch (e) {
    /* storage can be blocked; the 30 second refresh still catches up */
  }
}

const onVisible = () => {
  if (document.visibilityState === "visible") refreshUnread();
};
const onStorage = (event) => {
  if (event.key === STORAGE_KEY) refreshUnread();
};

function start() {
  refreshUnread();
  timer = setInterval(() => {
    if (document.visibilityState === "visible") refreshUnread();
  }, POLL_MS);
  window.addEventListener("focus", refreshUnread);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("storage", onStorage);
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = () => refreshUnread();
  }
}

function stop() {
  clearInterval(timer);
  timer = null;
  window.removeEventListener("focus", refreshUnread);
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("storage", onStorage);
  if (channel) {
    channel.close();
    channel = null;
  }
}

export default function useInboxUnread() {
  const [value, setValue] = useState(state);
  useEffect(() => {
    listeners.add(setValue);
    if (listeners.size === 1) start();
    else setValue(state);
    return () => {
      listeners.delete(setValue);
      if (listeners.size === 0) stop();
    };
  }, []);
  return value;
}
